/**
 * Passage exceptionnel / urgence — remplace la création manuelle de descente (UI uniquement).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, ChevronRight, Info } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../../lib/axios";

type ClientOption = { id: number; label: string };
type ZoneOption = { id: number; nom?: string };

const NIVEAUX = ["NORMAL", "URGENT", "CRITIQUE"] as const;

function useAdminPrefix(): string {
  const { pathname } = useLocation();
  return pathname.startsWith("/admin") ? "/admin" : "";
}

export default function PassageUrgencePage(): React.JSX.Element {
  const metierBase = useAdminPrefix() || "/admin";

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [zones, setZones] = useState<ZoneOption[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  const [clientId, setClientId] = useState("");
  const [motif, setMotif] = useState("");
  const [niveauUrgence, setNiveauUrgence] = useState<(typeof NIVEAUX)[number]>("URGENT");
  const [dateDemandee, setDateDemandee] = useState("");
  const [creneau, setCreneau] = useState("");
  const [adresse, setAdresse] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [photosUrlsText, setPhotosUrlsText] = useState("");
  const [commentaireInterne, setCommentaireInterne] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadRefs = useCallback(async () => {
    setLoadingRefs(true);
    try {
      const [cRes, zRes] = await Promise.all([
        api.get("/clients", { params: { page: 1, limit: 500 } }),
        api.get("/zones"),
      ]);
      const raw = Array.isArray(cRes.data?.data) ? cRes.data.data : [];
      const mapped: ClientOption[] = (raw as Record<string, unknown>[]).map((row) => {
        const id = Number(row.id);
        const nomEnt = String(row.nomEntreprise ?? "").trim();
        const nom = String(row.nom ?? "").trim();
        const prenom = String(row.prenom ?? "").trim();
        const label =
          nomEnt ||
          [nom, prenom].filter(Boolean).join(" ").trim() ||
          `Client #${id}`;
        return { id, label };
      });
      setClients(mapped);

      const zRaw = Array.isArray(zRes.data) ? zRes.data : zRes.data?.data ?? [];
      setZones(zRaw as ZoneOption[]);
    } catch {
      toast.error("Impossible de charger clients ou zones.");
      setClients([]);
      setZones([]);
    } finally {
      setLoadingRefs(false);
    }
  }, []);

  useEffect(() => {
    void loadRefs();
  }, [loadRefs]);

  const zoneLabel = useMemo(() => {
    if (!zoneId.trim()) return "";
    const z = zones.find((x) => String(x.id) === zoneId.trim());
    return z?.nom ?? `Zone #${zoneId}`;
  }, [zoneId, zones]);

  const buildMotifPayload = (): string => {
    const parts: string[] = [motif.trim()];
    if (commentaireInterne.trim()) {
      parts.push(`[Interne] ${commentaireInterne.trim()}`);
    }
    if (zoneLabel) {
      parts.push(`[Zone] ${zoneLabel}`);
    }
    return parts.filter(Boolean).join("\n\n");
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const cid = Number(clientId);
    if (!Number.isInteger(cid) || cid <= 0) {
      toast.warning("Sélectionnez un client.");
      return;
    }
    if (!motif.trim()) {
      toast.warning("Le motif d’urgence est obligatoire.");
      return;
    }

    const photosUrls = photosUrlsText
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    const body: Record<string, unknown> = {
      clientId: cid,
      typeDemande: "INTERVENTION_URGENTE",
      motif: buildMotifPayload(),
      niveauUrgence,
      ...(dateDemandee.trim()
        ? { nouvelleDateDemandee: new Date(dateDemandee).toISOString() }
        : {}),
      ...(creneau.trim() ? { creneauDemande: creneau.trim() } : {}),
      ...(adresse.trim() ? { adresseDemande: adresse.trim() } : {}),
      ...(photosUrls.length ? { photosUrls } : {}),
    };

    setSubmitting(true);
    try {
      await api.post("/demandes-client", body);
      toast.success("Demande urgente enregistrée.");
      setMotif("");
      setCommentaireInterne("");
      setPhotosUrlsText("");
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && (err.response?.status === 404 || err.response?.status === 405)) {
        toast.error("Endpoint à connecter : POST /api/demandes-client (création admin) n’est pas encore exposé.");
      } else {
        toast.error(
          axios.isAxiosError(err)
            ? String(err.response?.data?.error ?? err.response?.data?.message ?? err.message)
            : "Enregistrement impossible."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <header className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-rose-950/25 p-5 shadow-xl md:p-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-rose-400/90">Urgence contrôlée</p>
        <h1 className="mt-1 text-2xl font-bold text-white md:text-3xl">Passage exceptionnel</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Créer une demande urgente contrôlée, sans contourner le moteur Passage/Tournée.
        </p>
        <Link
          to={`${metierBase}/planificateur`}
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-emerald-400 hover:underline"
        >
          Retour planning
          <ChevronRight className="h-4 w-4" />
        </Link>
      </header>

      <div className="flex gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/10 p-4 text-sm text-amber-100">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
        <div className="space-y-2">
          <p>
            <strong>Ce passage n’est pas directement facturable.</strong>
          </p>
          <p>
            Il devra être affecté à une tournée, exécuté, prouvé et validé avant facturation.
          </p>
        </div>
      </div>

      <div className="flex gap-3 rounded-2xl border border-slate-700 bg-slate-900/50 p-4 text-sm text-slate-300">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p>
          La création passe par une <strong>demande</strong> (<code className="text-emerald-300">INTERVENTION_URGENTE</code>) tant que le backend n’expose pas d’endpoint dédié ; aucune descente manuelle.
        </p>
      </div>

      <form
        onSubmit={(ev) => void handleSubmit(ev)}
        className="space-y-5 rounded-2xl border border-slate-800 bg-slate-900/40 p-5 md:p-6"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Client <span className="text-rose-400">*</span>
            <select
              required
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              disabled={loadingRefs}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">— Sélectionner —</option>
              {clients.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.label} (#{c.id})
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Niveau d’urgence
            <select
              value={niveauUrgence}
              onChange={(e) => setNiveauUrgence(e.target.value as (typeof NIVEAUX)[number])}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              {NIVEAUX.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Motif d’urgence <span className="text-rose-400">*</span>
          <textarea
            required
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            rows={3}
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            placeholder="Contexte terrain, risques, contraintes…"
          />
        </label>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Date demandée
            <input
              type="date"
              value={dateDemandee}
              onChange={(e) => setDateDemandee(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Créneau demandé
            <input
              type="text"
              value={creneau}
              onChange={(e) => setCreneau(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="ex. 08h–12h"
            />
          </label>
        </div>

        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Adresse d’intervention
          <input
            type="text"
            value={adresse}
            onChange={(e) => setAdresse(e.target.value)}
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Zone (référentiel)
          <select
            value={zoneId}
            onChange={(e) => setZoneId(e.target.value)}
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
          >
            <option value="">— Optionnel —</option>
            {zones.map((z) => (
              <option key={z.id} value={String(z.id)}>
                {z.nom ?? `Zone #${z.id}`}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Photos (URLs, séparées par virgule ou retour ligne)
          <textarea
            value={photosUrlsText}
            onChange={(e) => setPhotosUrlsText(e.target.value)}
            rows={2}
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            placeholder="https://…"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Commentaire interne (non visible client)
          <textarea
            value={commentaireInterne}
            onChange={(e) => setCommentaireInterne(e.target.value)}
            rows={2}
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
          />
        </label>

        <button
          type="submit"
          disabled={submitting || loadingRefs}
          className="w-full rounded-xl bg-rose-600 py-3 text-sm font-semibold text-white hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto md:px-8"
        >
          {submitting ? "Envoi…" : "Créer demande urgente"}
        </button>
      </form>
    </div>
  );
}
